import { logError } from "./logging";

// Recovery from a stale bundle.
//
// The app is code-split, so pages load as hashed chunks on demand. Deploys sync
// the new build to S3 with --delete, which removes the previous build's chunks.
// A tab that was open across a deploy still holds the old module graph, so the
// next lazy import asks for a chunk that no longer exists. S3 answers with the
// SPA fallback — index.html, 200, text/html — and the browser refuses it for
// failing the module MIME check. The import rejects and whatever error boundary
// is nearest shows a generic failure, most visibly on the exercise editor.
//
// Vite fires `vite:preloadError` when a preloaded chunk fails to resolve, which
// is exactly this case. Reloading picks up the current index.html and the new
// chunk names, so the user recovers instead of hitting a dead page.

const RELOAD_MARKER = "talaera:stale-chunk-reloaded-at";
// Long enough that a genuinely broken deploy cannot spin, short enough that a
// tab left open for days can still recover from a later deploy.
const RELOAD_COOLDOWN_MS = 60 * 1000;

function reloadedRecently(): boolean {
  try {
    const last = window.sessionStorage.getItem(RELOAD_MARKER);
    return !!last && Date.now() - Number(last) < RELOAD_COOLDOWN_MS;
  } catch {
    // Storage can throw in private mode or with cookies blocked. Treat it as
    // "already reloaded" so a failure here can never cause a reload loop.
    return true;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    // Ignore — see reloadedRecently.
  }
}

/**
 * Reload once when a lazily imported chunk cannot be fetched, which means this
 * tab is running against a bundle that is no longer deployed.
 */
export function initStaleChunkRecovery(): void {
  window.addEventListener("vite:preloadError", event => {
    if (reloadedRecently()) {
      // Already tried. Let the error propagate so the boundary shows something
      // rather than reloading forever against a genuinely broken deploy.
      logError("Chunk load failed again after reload; not retrying", {
        payload: String((event as { payload?: unknown }).payload),
      });
      return;
    }

    event.preventDefault();
    markReloaded();
    window.location.reload();
  });
}
